import {HalContext, HalImage, HalImageDataType, HalPainterImageCompositorRef, HalPainterParametersType, HalRef} from "@cm/hal"
import {assertNever} from "@cm/utils"
import {Size2Like, Vector2Like} from "@cm/math"
import {AddressMode, ChannelLayout, halPainterParameters} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/basic-definitions"
import {getHalAddressMode} from "@cm/webgl2"
import {z} from "zod"
import {getChannelLayoutByCount} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/hal-utils"

export const reduceOperator = z.union([
    z.literal("sum"),
    z.literal("min"),
    z.literal("max"),
    z.literal("mean"),
    z.literal("sum-square"),
    z.literal("mean-square"),
    z.literal("root-mean-square"),
    z.literal("custom"),
])
export type ReduceOperator = z.infer<typeof reduceOperator>

export const shaderDefinition = z.object({
    code: z.string(),
    parameters: halPainterParameters.optional(),
    resultChannels: z.number().optional(), // default: sourceImage.descriptor.channelLayout
})
export type ShaderDefinition = z.infer<typeof shaderDefinition>

export type Args = {
    operator: ReduceOperator
    // only required for operator: "custom"
    custom?: {
        reduce: ShaderDefinition // 2-tuple reduction function with signature: vec4 reduce(vec4 a, vec4 b)
        preProcess?: ShaderDefinition // pre-process function with signature: vec4 preProcess(vec4 value, ivec2 index)
        postProcess?: ShaderDefinition // post-process function with signature: vec4 postProcess(vec4 value, ivec2 index)
    }
}

export class ReductionHelperHal {
    constructor(
        private halContext: HalContext,
        {operator, custom}: Args,
    ) {
        if (operator === "custom" && !custom) {
            throw new Error("Custom reduce operator requires custom parameters")
        }
        const createShaderInfo = (code: string, resultChannels?: number): CompiledShaderInfo => ({
            shader: halContext.createHalPainterImageCompositor(code),
            resultChannelLayout: resultChannels !== undefined ? getChannelLayoutByCount(resultChannels) : undefined,
        })
        // preprocess
        let preProcessFn: ShaderDefinition | undefined = undefined
        switch (operator) {
            case "sum-square":
            case "mean-square":
            case "root-mean-square":
                preProcessFn = {
                    code: `
                        vec4 preProcess(vec4 value, ivec2 index) {
                            return value * value;
                        }`,
                }
                break
            case "custom":
                preProcessFn = custom!.preProcess
                break
            default:
                break
        }
        const preprocessShaderInfo = preProcessFn
            ? createShaderInfo(
                  `
                    ${preProcessFn.code}
                    
                    vec4 computeColor(ivec2 targetPixel) {
                        vec4 value = texelFetch0(targetPixel);
                        return preProcess(value, targetPixel);
                    }
                `,
                  preProcessFn.resultChannels,
              )
            : undefined

        // postprocess
        let postProcessFn: ShaderDefinition | undefined = undefined
        switch (operator) {
            case "mean":
            case "mean-square":
            case "root-mean-square": {
                postProcessFn = {
                    code: `
                    uniform ivec2 u_sourceSize;
                    uniform ivec2 u_patchSize;

                    vec4 postProcess(vec4 value, ivec2 index) {
                        ivec2 currentPatchSize = min(u_patchSize, u_sourceSize - index * u_patchSize);
                        vec4 mean = value / float(currentPatchSize.x * currentPatchSize.y);
                        ${operator === "root-mean-square" ? "return sqrt(mean);" : "return mean;"}
                    }`,
                }
                break
            }
            case "custom":
                postProcessFn = custom!.postProcess
                break
            default:
                break
        }
        const postprocessShaderInfo = postProcessFn
            ? createShaderInfo(
                  `
                    ${postProcessFn.code}
                    
                    vec4 computeColor(ivec2 targetPixel) {
                        vec4 value = texelFetch0(targetPixel);
                        return postProcess(value, targetPixel);
                    }
                `,
                  postProcessFn.resultChannels,
              )
            : undefined

        // we successively down-sample to 1x1
        let reduceFn: ShaderDefinition
        const wrapInReduceFn = (accuFn: string): ShaderDefinition => ({
            code: `
            vec4 reduce(vec4 a, vec4 b) {
                return ${accuFn};
            }`,
        })
        switch (operator) {
            case "sum":
            case "sum-square":
            case "mean":
            case "mean-square":
            case "root-mean-square":
                reduceFn = wrapInReduceFn("a + b")
                break
            case "min":
                reduceFn = wrapInReduceFn("min(a, b)")
                break
            case "max":
                reduceFn = wrapInReduceFn("max(a, b)")
                break
            case "custom":
                reduceFn = custom!.reduce
                break
            default:
                assertNever(operator)
        }

        const reduceShaderInfo = createShaderInfo(
            `
            uniform ivec2 u_patchSize;
            uniform int u_addressMode;
        
            ${reduceFn.code}
            
            vec4 computeColor(ivec2 targetPixel) {
                ivec2 sourcePixel = targetPixel * 2;
                // if the patch size is odd we need to adjust the source pixel
                ivec2 targetPatchSize = ivec2(ceil(vec2(u_patchSize) * 0.5));
                if ((u_patchSize.x & 1) != 0) {
                    sourcePixel.x -= targetPixel.x / targetPatchSize.x;
                }
                if ((u_patchSize.y & 1) != 0) {
                    sourcePixel.y -= targetPixel.y / targetPatchSize.y;
                }
                // check if neighbors are still in the same patch
                bool samePatchX = (sourcePixel.x + 1) % u_patchSize.x != 0;
                bool samePatchY = (sourcePixel.y + 1) % u_patchSize.y != 0;
                vec4 c00 = texelFetch0(sourcePixel + ivec2(0, 0), u_addressMode);
                if (samePatchX && samePatchY) {
                    vec4 c01 = texelFetch0(sourcePixel + ivec2(0, 1), u_addressMode);
                    vec4 c10 = texelFetch0(sourcePixel + ivec2(1, 0), u_addressMode);
                    vec4 c11 = texelFetch0(sourcePixel + ivec2(1, 1), u_addressMode);
                    vec4 c0 = reduce(c00, c01);
                    vec4 c1 = reduce(c10, c11);
                    return reduce(c0, c1);
                }
                else if (samePatchX) {
                    vec4 c10 = texelFetch0(sourcePixel + ivec2(1, 0), u_addressMode);
                    return reduce(c00, c10);
                }
                else if (samePatchY) {
                    vec4 c01 = texelFetch0(sourcePixel + ivec2(0, 1), u_addressMode);
                    return reduce(c00, c01);
                }
                else {
                    return c00;
                }
            }
            `,
            reduceFn.resultChannels,
        )
        this._data = {
            preprocessShaderInfo,
            postprocessShaderInfo,
            reduceShaderInfo,
        }
    }

    dispose() {
        if (!this._data) {
            throw new Error("ReductionHelperHal: Already disposed")
        }
        this._data.preprocessShaderInfo?.shader.release()
        this._data.postprocessShaderInfo?.shader.release()
        this._data.reduceShaderInfo.shader.release()
        this._data = null
    }

    reduce(args: {
        source: HalImage
        target: HalImage
        options?: {
            numPatches?: Vector2Like // default: {x: 1, y: 1}; number of patches in each dimension
            patchSize?: Size2Like // default: {width: sourceImage.descriptor.width, height: sourceImage.descriptor.height}; size of the patch to reduce
            patchFillAddressMode?: AddressMode // default: "border"; address mode for filling out-of-bounds pixels
        }
    }) {
        if (!this._data) {
            throw new Error("ReductionHelperHal: Already disposed")
        }
        const addressMode = args.options?.patchFillAddressMode ?? "border"
        let sourceImage = new HalRef(args.source)
        sourceImage.addRef()
        const sourceImageIsFullyReduced = sourceImage.ref.descriptor.width === 1 && sourceImage.ref.descriptor.height === 1
        const sourceChannelLayout = sourceImage.ref.descriptor.channelLayout
        const numPatches =
            args.options?.numPatches ??
            (args.options?.patchSize
                ? {
                      x: Math.ceil(sourceImage.ref.descriptor.width / args.options.patchSize.width),
                      y: Math.ceil(sourceImage.ref.descriptor.height / args.options.patchSize.height),
                  }
                : {x: 1, y: 1})
        let patchSize = args.options?.patchSize ?? {
            width: Math.ceil(sourceImage.ref.descriptor.width / numPatches.x),
            height: Math.ceil(sourceImage.ref.descriptor.height / numPatches.y),
        }
        const intermediateDataType: HalImageDataType = "float32"
        const parameters: HalPainterParametersType = {
            u_sourceSize: {type: "int2", value: {x: sourceImage.ref.descriptor.width, y: sourceImage.ref.descriptor.height}, isOptional: true},
            u_patchSize: {type: "int2", value: {x: patchSize.width, y: patchSize.height}, isOptional: true},
        }
        if (this._data.preprocessShaderInfo) {
            const preprocessedImage = this.halContext.createPhysicalImage({
                width: sourceImage.ref.descriptor.width,
                height: sourceImage.ref.descriptor.height,
                channelLayout: this._data.preprocessShaderInfo.resultChannelLayout ?? sourceChannelLayout,
                dataType: intermediateDataType,
            })
            this._data.preprocessShaderInfo.shader.ref.paint({
                parameters,
                sourceImages: [sourceImage.ref],
                target: preprocessedImage.ref,
            })
            sourceImage.release()
            sourceImage = preprocessedImage
        }
        for (let iteration = 0; ; iteration++) {
            if (!Number.isInteger(patchSize.width) || !Number.isInteger(patchSize.height)) {
                throw new Error("Batch size must be a divisor of the image size")
            }
            if (patchSize.width === 1 && patchSize.height === 1) {
                break
            }
            const downSampledPatchSize = {
                width: Math.ceil(patchSize.width / 2),
                height: Math.ceil(patchSize.height / 2),
            }
            const downSampledSize = {
                width: downSampledPatchSize.width * numPatches.x,
                height: downSampledPatchSize.height * numPatches.y,
            }
            const useTarget = !this._data.postprocessShaderInfo && downSampledPatchSize.width === 1 && downSampledPatchSize.height === 1
            const nextSourceImage = useTarget
                ? new HalRef(args.target)
                : this.halContext.createPhysicalImage({
                      ...downSampledSize,
                      channelLayout: this._data.reduceShaderInfo.resultChannelLayout ?? sourceChannelLayout,
                      dataType: intermediateDataType,
                  })
            this._data.reduceShaderInfo.shader.ref.paint({
                parameters: {
                    ...parameters,
                    u_patchSize: {type: "int2", value: {x: patchSize.width, y: patchSize.height}},
                    u_addressMode: {type: "int", value: getHalAddressMode(addressMode)},
                },
                sourceImages: [sourceImage.ref],
                target: nextSourceImage.ref,
            })
            sourceImage.release()
            sourceImage = nextSourceImage
            patchSize = downSampledPatchSize
        }
        if (this._data.postprocessShaderInfo) {
            this._data.postprocessShaderInfo.shader.ref.paint({
                parameters: parameters,
                sourceImages: [sourceImage.ref],
                target: args.target,
            })
        } else {
            if (sourceImageIsFullyReduced) {
                // copy the result to the target
                this.halContext.blit({
                    sourceImage: args.source,
                    targetImage: args.target,
                })
            }
        }
        sourceImage.release()
    }

    private _data: Data | null
}

type Data = {
    preprocessShaderInfo?: CompiledShaderInfo
    postprocessShaderInfo?: CompiledShaderInfo
    reduceShaderInfo: CompiledShaderInfo
}

type CompiledShaderInfo = {
    shader: HalPainterImageCompositorRef
    resultChannelLayout?: ChannelLayout
}
